import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import { AlertTriangle, Check, Loader2, Search, ShieldCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

// The Brand check of one part of the book (a chapter, the letter, or About
// the Author): an AI review against Kingdom Factor's Statement of Faith and
// Core Values. It never edits; each flag has the words, the reason, and a fix.
//
// `locate` (useFlagHighlights) finds each flag's words on the page: the flag
// gets a "Show in …" button (`showText`), and a click on flagged words in the
// text brings its flag here (`hint` says so).
export function BrandCheckCard({ bookId, brand, notCheckedText, fixText, locked = false, locate = null, showText = "Show in chapter", hint = null }) {
  const [lit, setLit] = useState(null)
  const picked = locate?.picked
  useEffect(() => {
    if (!picked) return undefined
    document.getElementById(`brand-flag-${picked.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })
    setLit(picked.id)
    const timer = setTimeout(() => setLit(null), 2500)
    return () => clearTimeout(timer)
  }, [picked])

  const keep = (flag) => {
    const warning = flag.severity === "serious"
      ? "Keep these words? Kingdom Factor will read them and decide. The book cannot go to print until they clear the flag."
      : "Keep these words?"
    if (window.confirm(warning)) {
      router.post(`/app/coach/books/${bookId}/brand_flags/${flag.id}/keep`, {}, { preserveScroll: true })
    }
  }

  return (
    <Card id="brand-check" className="scroll-mt-4">
      <CardContent className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <b className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-kf-red" /> Brand check</b>
          {brand.status === "done" && brand.flags.length === 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Check className="h-3.5 w-3.5" /> No flags</span>
          )}
          {brand.status === "done" && brand.flags.length > 0 && (
            <span className="text-xs text-zinc-600">{brand.flags.length} {brand.flags.length === 1 ? "flag" : "flags"}</span>
          )}
        </div>
        {brand.status === "not_checked" && <p className="text-xs text-zinc-600">{notCheckedText}</p>}
        {brand.status === "checking" && (
          <p className="inline-flex items-center gap-1.5 text-xs text-zinc-600"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading it against Kingdom Factor's values. About a minute.</p>
        )}
        {brand.status === "failed" && <p className="text-xs text-red-600">{brand.error}</p>}
        {hint && brand.flags.length > 0 && <p className="text-xs text-zinc-600">{hint}</p>}
        {brand.status === "done" && brand.flags.length === 0 && (
          <p className="text-xs text-zinc-600">Nothing here conflicts with Kingdom Factor's Statement of Faith or Core Values.</p>
        )}
        {brand.flags.map((flag) => (
          <div key={flag.id} id={`brand-flag-${flag.id}`}
            className={`scroll-mt-4 space-y-1.5 rounded-md border p-3 transition-shadow ${flag.severity === "serious" ? "border-red-200 bg-red-50/50" : "border-amber-200 bg-amber-50/50"} ${lit === flag.id ? "ring-2 ring-kf-red" : ""}`}>
            <div className="flex items-center gap-2 text-xs">
              <span className={`rounded-full px-2 py-0.5 font-semibold ${flag.severity === "serious" ? "bg-red-600 text-white" : "bg-amber-200 text-amber-900"}`}>
                {flag.severity === "serious" ? "Serious" : "Note"}
              </span>
              <span className="text-zinc-700">{flag.topic}</span>
            </div>
            <p className="italic text-zinc-900">“{flag.quote}”</p>
            {locate?.places && <FlagPlace place={locate.places[flag.id]} onShow={() => locate.show(flag.id)} showText={showText} />}
            <p className="text-xs text-zinc-700">{flag.reason}</p>
            {flag.suggestion && <p className="text-xs text-zinc-700"><b>Suggested fix:</b> {flag.suggestion}</p>}
            {flag.cleared && <p className="inline-flex items-center gap-1 text-xs text-emerald-700"><Check className="h-3.5 w-3.5" /> Cleared by Kingdom Factor</p>}
            {flag.held && !flag.cleared && <p className="inline-flex items-center gap-1 text-xs text-red-700"><AlertTriangle className="h-3.5 w-3.5" /> Kingdom Factor put the book on hold. Change these words to go to print.</p>}
            {flag.awaitingAdmin && <p className="text-xs text-zinc-600">You kept these words. Kingdom Factor will read them and decide.</p>}
            {flag.kept && flag.severity === "note" && <p className="text-xs text-zinc-600">You kept these words.</p>}
            {!flag.kept && !flag.cleared && !locked && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => keep(flag)}>Keep my words</Button>
                <span className="text-xs text-zinc-500">{fixText}</span>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

// Where a flag's words are on the page, or that they are not there.
export function FlagPlace({ place, onShow, showText }) {
  if (!place) return <p className="text-xs text-zinc-500">These words are not on this page now.</p>
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <button type="button" onClick={onShow}
        className="inline-flex items-center gap-1 font-semibold text-kf-red underline-offset-4 hover:underline">
        <Search className="h-3.5 w-3.5" /> {showText}
      </button>
      {place.section && <span className="text-zinc-600">In “{place.section}”</span>}
    </div>
  )
}
